import React, { useState } from "react";
import { ArrowLeft, Plus, X, Download, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const GratitudeGardenGame = ({ onBack }) => {
  const [gratitudes, setGratitudes] = useState([]);
  const [newGratitude, setNewGratitude] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [selectedFlower, setSelectedFlower] = useState(null);

  const flowerTypes = [
    { emoji: "🌻", name: "Sunflower", color: "#FCD34D" },
    { emoji: "🌹", name: "Rose", color: "#F87171" },
    { emoji: "🌷", name: "Tulip", color: "#A78BFA" },
    { emoji: "🌺", name: "Hibiscus", color: "#FB7185" },
    { emoji: "🌸", name: "Cherry Blossom", color: "#F9A8D4" },
    { emoji: "🌼", name: "Daisy", color: "#FBBF24" },
    { emoji: "🌿", name: "Herb", color: "#34D399" },
    { emoji: "🏵️", name: "Rosette", color: "#F97316" },
  ];

  const getRandomFlower = () => {
    return flowerTypes[Math.floor(Math.random() * flowerTypes.length)];
  };

  const getRandomPosition = () => {
    return {
      x: Math.random() * 80 + 10, // 10-90% from left
      y: Math.random() * 70 + 20, // 20-90% from top
    };
  };

  const addGratitude = () => {
    if (newGratitude.trim()) {
      const flower = getRandomFlower();
      const position = getRandomPosition();

      const newGratitudeItem = {
        id: Date.now(),
        text: newGratitude.trim(),
        flower: flower,
        position: position,
        planted: new Date(),
        size: Math.random() * 0.5 + 0.8, // 0.8 to 1.3 scale
      };

      setGratitudes((prev) => [...prev, newGratitudeItem]);
      setNewGratitude("");
      setShowInput(false);
    }
  };

  const removeGratitude = (id) => {
    setGratitudes((prev) => prev.filter((g) => g.id !== id));
    setSelectedFlower(null);
  };

  const downloadGarden = () => {
    const gardenData = {
      gratitudes: gratitudes.map((g) => ({
        text: g.text,
        flower: g.flower.name,
        planted: g.planted,
      })),
      totalFlowers: gratitudes.length,
      createdAt: new Date(),
    };

    const dataStr = JSON.stringify(gardenData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(dataBlob);
    link.download = "my-gratitude-garden.json";
    link.click();
  };

  const gardenStats = {
    totalFlowers: gratitudes.length,
    uniqueTypes: new Set(gratitudes.map((g) => g.flower.name)).size,
    oldestFlower:
      gratitudes.length > 0
        ? Math.min(...gratitudes.map((g) => g.planted))
        : null,
  };

  return (
    <div className="min-h-screen p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-white/80 rounded-xl hover:bg-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-sm sm:text-base">Back</span>
        </button>

        <h1 className="text-lg sm:text-2xl font-bold text-gray-800 text-center flex-1 mx-2">
          <span className="hidden sm:inline">🌻 Gratitude Garden</span>
          <span className="sm:hidden">🌻 Garden</span>
        </h1>

        <div className="flex items-center gap-2">
          <button
            onClick={downloadGarden}
            className="bg-green-500 hover:bg-green-600 text-white px-2 sm:px-3 py-2 rounded-lg flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
          >
            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Save Garden</span>
            <span className="sm:hidden">Save</span>
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto">
        {/* Controls */}
        <div className="lg:w-80 space-y-6">
          {/* Add Gratitude */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Plant a Flower
            </h3>

            {!showInput ? (
              <button
                onClick={() => setShowInput(true)}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white py-3 rounded-lg font-medium transition-all"
              >
                Add Something You're Grateful For
              </button>
            ) : (
              <div className="space-y-3">
                <textarea
                  value={newGratitude}
                  onChange={(e) => setNewGratitude(e.target.value)}
                  placeholder="I'm grateful for..."
                  className="w-full p-3 border border-gray-300 rounded-lg resize-none h-20 focus:outline-none focus:ring-2 focus:ring-green-500"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={addGratitude}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg font-medium"
                  >
                    Plant Flower
                  </button>
                  <button
                    onClick={() => {
                      setShowInput(false);
                      setNewGratitude("");
                    }}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              Garden Stats
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Flowers:</span>
                <span className="font-medium">{gardenStats.totalFlowers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Flower Types:</span>
                <span className="font-medium">{gardenStats.uniqueTypes}</span>
              </div>
              {gardenStats.oldestFlower && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Garden Started:</span>
                  <span className="font-medium text-sm">
                    {new Date(gardenStats.oldestFlower).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {selectedFlower && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Flower Details</h3>
                <button
                  onClick={() => removeGratitude(selectedFlower.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">
                    {selectedFlower.flower.emoji}
                  </span>
                  <span className="font-medium">
                    {selectedFlower.flower.name}
                  </span>
                </div>
                <p className="text-gray-700 bg-white/60 p-3 rounded-lg">
                  "{selectedFlower.text}"
                </p>
                <p className="text-xs text-gray-500">
                  Planted:{" "}
                  {new Date(selectedFlower.planted).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3">How to Use:</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Write something you're grateful for</li>
              <li>• Watch a beautiful flower grow in your garden</li>
              <li>• Click on flowers to read your gratitudes</li>
              <li>• Build your garden over time</li>
              <li>• Save and share your gratitude garden</li>
            </ul>
          </div>
        </div>

        {/* Garden */}
        <div className="flex-1">
          <div className="bg-gradient-to-b from-sky-200 to-green-200 rounded-2xl p-4 sm:p-8 shadow-lg relative min-h-80 sm:min-h-96 overflow-hidden">
            {/* Sky background */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-200 via-green-100 to-green-200 rounded-2xl" />

            {/* Ground */}
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-green-300 to-green-200 rounded-b-2xl" />

            {/* Sun */}
            <div className="absolute top-4 right-4 w-12 h-12 bg-yellow-300 rounded-full shadow-lg" />

            {/* Clouds */}
            <div className="absolute top-8 left-8 w-16 h-8 bg-white/60 rounded-full" />
            <div className="absolute top-12 left-12 w-12 h-6 bg-white/40 rounded-full" />

            {/* Flowers */}
            <AnimatePresence>
              {gratitudes.map((gratitude) => (
                <motion.div
                  key={gratitude.id}
                  initial={{ scale: 0, y: 20 }}
                  animate={{ scale: gratitude.size, y: 0 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.5, type: "spring" }}
                  className="absolute cursor-pointer select-none hover:scale-110 transition-transform"
                  style={{
                    left: `${gratitude.position.x}%`,
                    top: `${gratitude.position.y}%`,
                  }}
                  onClick={() => setSelectedFlower(gratitude)}
                >
                  <div className="relative">
                    {/* Stem */}
                    <div className="w-1 h-8 bg-green-500 mx-auto" />
                    {/* Flower */}
                    <div
                      className="text-4xl hover:animate-bounce"
                      style={{
                        filter:
                          selectedFlower?.id === gratitude.id
                            ? "brightness(1.2)"
                            : "none",
                      }}
                    >
                      {gratitude.flower.emoji}
                    </div>
                    {/* Leaves */}
                    <div className="absolute top-4 -left-2 text-green-500 text-sm rotate-45">
                      🍃
                    </div>
                    <div className="absolute top-6 -right-2 text-green-500 text-sm -rotate-45">
                      🍃
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {gratitudes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-gray-600">
                  <div className="text-6xl mb-4">🌱</div>
                  <p className="text-lg font-medium">Your garden is waiting!</p>
                  <p className="text-sm">
                    Plant your first flower by adding something you're grateful
                    for
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GratitudeGardenGame;
